"use client";

import React from "react";
import { reportBoundary } from "@/lib/errorReporter";

type Props = {
  children: React.ReactNode;
  /** Label helps us tell root-vs-inner boundary apart in logs. */
  label?: string;
  /** When this value changes, the boundary resets (useful keyed by workspaceId). */
  resetKey?: string | number | null;
};

type State = { error: Error | null; componentStack: string | null };

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): State {
    return { error, componentStack: null };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    this.setState({ componentStack: info.componentStack ?? null });
    reportBoundary({
      message: `[${this.props.label ?? "root"}] ${error.message}`,
      stack: error.stack,
      componentStack: info.componentStack ?? undefined,
    });
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, componentStack: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const msg = this.state.error.message || "Unknown error";
    const stack = this.state.error.stack || "";
    const componentStack = this.state.componentStack || "";
    return (
      <div className="flex-1 min-h-0 overflow-y-auto p-6">
        <div className="max-w-3xl mx-auto rounded-xl border border-[var(--border)] bg-[var(--bg-secondary)] p-5">
          <div className="text-[var(--red)] text-[13px] font-semibold mb-2">
            Something went wrong in this view
          </div>
          <div className="text-[12px] text-[var(--fg-tertiary)] font-mono break-words mb-4">
            {msg}
          </div>
          <details className="mb-3" open>
            <summary className="text-[11px] uppercase tracking-wide text-[var(--fg-muted)] cursor-pointer mb-1">
              Component stack
            </summary>
            <pre className="text-[10.5px] font-mono bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md p-2.5 whitespace-pre-wrap break-words text-[var(--fg-tertiary)] max-h-60 overflow-y-auto">
              {componentStack || "(not captured)"}
            </pre>
          </details>
          {stack && (
            <details className="mb-4">
              <summary className="text-[11px] uppercase tracking-wide text-[var(--fg-muted)] cursor-pointer mb-1">
                JS stack
              </summary>
              <pre className="text-[10.5px] font-mono bg-[var(--bg-tertiary)] border border-[var(--border)] rounded-md p-2.5 whitespace-pre-wrap break-words text-[var(--fg-tertiary)] max-h-60 overflow-y-auto">
                {stack}
              </pre>
            </details>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => this.setState({ error: null, componentStack: null })}
              className="px-3 py-1.5 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[12px] font-medium"
            >
              Try again
            </button>
            <button
              onClick={() => {
                if (typeof window !== "undefined") window.location.assign("/");
              }}
              className="px-3 py-1.5 rounded-md bg-[var(--bg-tertiary)] border border-[var(--border-strong)] hover:bg-[var(--bg-hover)] text-[12px] font-medium"
            >
              Back to home
            </button>
            <button
              onClick={() => {
                if (typeof window !== "undefined") window.location.reload();
              }}
              className="px-3 py-1.5 rounded-md bg-[var(--bg-tertiary)] border border-[var(--border-strong)] hover:bg-[var(--bg-hover)] text-[12px] font-medium"
            >
              Reload
            </button>
          </div>
        </div>
      </div>
    );
  }
}
